/**
 * Track Mobile — the icon set (design_handoff_track_mobile §10).
 *
 * "Icons are 1.8 stroke line icons, 20–26 px, drawn inline in the source.
 * Lucide equivalents are acceptable ONLY if they match the stroke weight and
 * size." Rather than find equivalents and hope, every path here is transcribed
 * from the prototype, with its own stroke width and default size, because a few
 * of them deliberately break the 1.8 rule: the tick is 2.4, the plus 2.2, the
 * sparkle 2, the wordmark mark 2.
 *
 * Each icon takes a size so a caller can scale it where the design says a
 * different number, but the DEFAULT is the size the design uses most, so a
 * call site that says nothing is already right.
 */

import { liveClockSvg } from '../live-clock.js';

const svg = (d, { size, sw, box = 24, cap = 'round', join = true } = {}) => (s = size) =>
  `<svg width="${s}" height="${s}" viewBox="0 0 ${box} ${box}" fill="none" stroke="currentColor"`
  + ` stroke-width="${sw}" stroke-linecap="${cap}"${join ? ' stroke-linejoin="round"' : ''}`
  + ` style="flex:0 0 auto">${d.map((p) => `<path d="${p}"></path>`).join('')}</svg>`;

export const icon = {
  // Navigation and chrome
  chevronLeft: svg(['M15 6l-6 6 6 6'], { size: 24, sw: 1.8 }),
  chevronRight: svg(['M9 6l6 6-6 6'], { size: 22, sw: 1.8 }),
  close: svg(['M6 6l12 12M18 6L6 18'], { size: 14, sw: 2.4, join: false }),
  plus: svg(['M12 5v14M5 12h14'], { size: 18, sw: 2.2, join: false }),
  minus: svg(['M5 12h14'], { size: 28, sw: 2.2 }),
  search: svg(['M11 4a7 7 0 1 0 0.01 0 M20 20l-4-4'], { size: 20, sw: 1.8 }),
  tick: svg(['M5 12.5l4.5 4.5L19 7.5'], { size: 20, sw: 2.4 }),

  // Tab bar (25 px in the bar; the sparkle is 28 in the centre button)
  home: svg(['M4 11l8-7 8 7v9H4z M10 20v-5h4v5'], { size: 25, sw: 1.8 }),
  calendar: svg(['M4 6h16v14H4z M4 10h16 M8 3v4 M16 3v4 M8 14h2M14 14h2M8 17h2'], { size: 25, sw: 1.8 }),
  tasks: svg(['M12 3a9 9 0 1 0 0.01 0 M8.5 12l2.5 2.5 4.5-5'], { size: 25, sw: 1.8 }),
  projects: svg(['M3.5 8h17v11h-17z M8.5 8V5h7v3'], { size: 25, sw: 1.8 }),
  sparkle: svg([
    'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z',
    'M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z',
  ], { size: 28, sw: 2 }),
  // The field and the Ask Track row use the single-star form, not the pair.
  sparkleSmall: svg(['M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z'], { size: 20, sw: 2 }),

  // Rows and actions
  phone: svg([
    'M6 4h3l1.5 4.5-2 1.3a10 10 0 0 0 5.7 5.7l1.3-2L20 15v3a2 2 0 0 1-2 2A15 15 0 0 1 4 6a2 2 0 0 1 2-2z',
  ], { size: 21, sw: 1.8 }),
  send: svg(['M4 11l16-7-7 16-2-7z'], { size: 26, sw: 1.8 }),
  clock: svg(['M12 3a9 9 0 1 0 0.01 0 M12 7v5l3 2'], { size: 26, sw: 1.8 }),
  // The Time TAB's clock: the same face, hands at the time now (Issue 25/10/2026).
  liveClock: (s = 26) => liveClockSvg({ size: s, sw: 1.8, style: 'flex:0 0 auto' }),
  receipt: svg(['M6 3h12v18l-3-2-3 2-3-2-3 2z M9 8h6M9 12h6'], { size: 25, sw: 1.8 }),
  card: svg(['M3.5 6h17v12h-17z M3.5 10h17 M7 14.5h3'], { size: 25, sw: 1.8 }),   // the contractor's Spend tab (v4 p71)
  camera: svg(['M4 8h3l2-3h6l2 3h3v11H4z M12 10.5a3 3 0 1 0 0.01 0'], { size: 26, sw: 1.8 }),
  folder: svg(['M3.5 6h6l2 2h9v11h-17z'], { size: 20, sw: 1.8 }),
  mail: svg(['M4 6h16v12H4z M4 7l8 6 8-6'], { size: 20, sw: 1.8 }),
  message: svg(['M4 5h16v11H9l-5 4z'], { size: 20, sw: 1.8 }),
  // Assets (docs/design_handoff_assets): the NFC glyph, at the boards' 1.8 stroke.
  nfc: svg(['M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4z', 'M6 8.5a8 8 0 0 1 12 0', 'M8.5 11a4.5 4.5 0 0 1 7 0', 'M12 13.5a1 1 0 1 0 0.01 0'], { size: 20, sw: 1.8 }),
  undo: svg(['M9 14 4 9l5-5', 'M4 9h11a5 5 0 0 1 0 10h-3'], { size: 20, sw: 1.8 }),
  personPlus: svg(['M9 4.5a3.5 3.5 0 1 0 0.01 0', 'M2.5 20a6.5 6.5 0 0 1 13 0', 'M17 8h5m-2.5-2.5v5'], { size: 20, sw: 1.8 }),
  warning: svg(['M12 3 2.5 19.5h19L12 3z', 'M12 10v4m0 3v.5'], { size: 20, sw: 1.8 }),
  barcode: svg(['M4 6v12 M7 6v12 M10.5 6v12 M14 6v12 M17 6v12 M20 6v12'], { size: 20, sw: 1.8 }),
  mic: svg([
    'M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3z M5 11a7 7 0 0 0 14 0 M12 18v3',
  ], { size: 24, sw: 1.8 }),
};

/** The Face ID frame on the money lock card (§5.9) — 40 px, stroke 1.6. */
export const faceIdIcon = (size = 40) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24"`
  + ' fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">'
  + '<path d="M4 8V6a2 2 0 0 1 2-2h2"></path><path d="M16 4h2a2 2 0 0 1 2 2v2"></path>'
  + '<path d="M20 16v2a2 2 0 0 1-2 2h-2"></path><path d="M8 20H6a2 2 0 0 1-2-2v-2"></path>'
  + '<path d="M9 9.5v1"></path><path d="M15 9.5v1"></path>'
  + '<path d="M12 9.5v3.5h-1"></path><path d="M9 15.5s1.2 1 3 1 3-1 3-1"></path></svg>';

/**
 * The Track mark and wordmark (§5.1, §10: "drawn in code … do not replace them
 * with an image"). Two strokes, a rose circle, and the wordmark's own rose
 * full stop. The 700 weight and −0.055em are the wordmark's alone — §3 rule 3
 * says nothing else in the app is bold.
 */
export const wordmark = () => `
  <div style="display:flex;align-items:center;gap:9px">
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" style="flex:0 0 22px">
      <path d="M3 7h16M3 15h10" stroke="var(--m-ink)" stroke-width="2" stroke-linecap="round"></path>
      <circle cx="17" cy="15" r="2.6" fill="var(--m-rose)"></circle>
    </svg>
    <span style="font-size:22px;font-weight:700;letter-spacing:-0.055em;line-height:1;color:var(--m-ink)"
      >Track<span style="color:var(--m-rose)">.</span></span>
  </div>`;

/** Status-bar signal bars. Dimmed to 35% when offline (§7). */
export const signalGlyph = (offline) => `
  <svg width="18" height="12" viewBox="0 0 18 12" fill="currentColor"
    style="opacity:${offline ? 0.35 : 1};transition:opacity 200ms var(--m-ease-out)">
    <rect x="0" y="8" width="3" height="4" rx="1"></rect>
    <rect x="5" y="5.5" width="3" height="6.5" rx="1"></rect>
    <rect x="10" y="3" width="3" height="9" rx="1"></rect>
    <rect x="15" y="0" width="3" height="12" rx="1" opacity="0.35"></rect>
  </svg>`;

export const batteryGlyph = () => `
  <svg width="26" height="12" viewBox="0 0 26 12" fill="none">
    <rect x="0.5" y="0.5" width="22" height="11" rx="3.5" stroke="currentColor" opacity="0.4"></rect>
    <rect x="2" y="2" width="15" height="8" rx="2" fill="currentColor"></rect>
    <rect x="24" y="4" width="1.6" height="4" rx="0.8" fill="currentColor" opacity="0.4"></rect>
  </svg>`;

/* ── Release D (docs/phone-release-d/BRIEF.md §3.9): the SF-Symbol-like set the
 * boards are drawn with, copied VERBATIM from lib.py ic() — 24-box, 1.8 strokes,
 * round caps. sf(name, size, sw) draws one in currentColor. */
const SF = {
  'chevron': '<path d="M9 6l6 6-6 6"/>',
  'chevron-l': '<path d="M15 6l-6 6 6 6"/>',
  'chevron-d': '<path d="M6 9l6 6 6-6"/>',
  'search': '<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>',
  'plus': '<path d="M12 5v14M5 12h14"/>',
  'more': '<circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none"/>',
  'home': '<path d="M4 11l8-7 8 7v9a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1z"/>',
  'folder': '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>',
  'tag': '<path d="M4 4h7l9 9-7 7-9-9z"/><circle cx="8.5" cy="8.5" r="1.2" fill="currentColor" stroke="none"/>',
  'check-circle': '<circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.5 2.5L16 9.5"/>',
  'check': '<path d="M5 12.5l4.5 4.5L19 7"/>',
  'menu': '<path d="M4 7h16M4 12h16M4 17h16"/>',
  'clock': '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  'calendar': '<rect x="3" y="5" width="18" height="16" rx="3"/><path d="M3 10h18M8 3v4M16 3v4"/>',
  'mail': '<rect x="3" y="5" width="18" height="14" rx="3"/><path d="M3 8l9 6 9-6"/>',
  'doc': '<path d="M6 3h8l5 5v13H6z"/><path d="M14 3v5h5"/>',
  'pound': '<path d="M8 20h9M9 20c2-2 2-5 1-8M7 12h7M16 7a3.5 3.5 0 0 0-6 0v5"/>',
  'cap': '<path d="M3 9l9-4 9 4-9 4z"/><path d="M7 11v5c2 2 8 2 10 0v-5"/>',
  'person': '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6"/>',
  'people': '<circle cx="9" cy="8" r="3.5"/><circle cx="17" cy="9" r="2.5"/><path d="M2 20c0-4 3-6 7-6s7 2 7 6M16 14c3 0 6 2 6 5"/>',
  'building': '<rect x="4" y="3" width="16" height="18" rx="1.5"/><path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2"/>',
  'cart': '<path d="M3 4h2l2 12h11l2-8H6"/><circle cx="9" cy="20" r="1.4"/><circle cx="17" cy="20" r="1.4"/>',
  'bank': '<path d="M3 9l9-5 9 5H3zM5 9v8M10 9v8M14 9v8M19 9v8M3 20h18"/>',
  'chart': '<path d="M4 20V10M10 20V4M16 20v-8M22 20H2"/>',
  'receipt': '<path d="M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5L6 21z"/><path d="M9 8h6M9 12h6"/>',
  'phone': '<path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z"/>',
  'pin': '<path d="M12 21s-6-6-6-11a6 6 0 0 1 12 0c0 5-6 11-6 11z"/><circle cx="12" cy="10" r="2"/>',
  'camera': '<path d="M4 8h3l2-3h6l2 3h3v11H4z"/><circle cx="12" cy="13" r="3.5"/>',
  'mic': '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/>',
  'sparkle': '<path d="M12 3l2 6 6 2-6 2-2 6-2-6-6-2 6-2z"/>',
  'arrow-r': '<path d="M5 12h14M13 6l6 6-6 6"/>',
  'circle': '<circle cx="12" cy="12" r="9"/>',
  'xmark': '<path d="M6 6l12 12M18 6L6 18"/>',
  'faceid': '<path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3"/><path d="M9 9v2M15 9v2M12 9v4h-1M9 15c1.5 1.3 4.5 1.3 6 0"/>',
  'lock': '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>',
  'nfc': '<path d="M6 8a8 8 0 0 1 0 8M9 10a5 5 0 0 1 0 4M15 10a5 5 0 0 0 0 4M18 8a8 8 0 0 0 0 8"/>',
  'wrench': '<path d="M14 4a5 5 0 0 0-4.6 7L4 16.4 7.6 20l5.4-5.4A5 5 0 0 0 20 10l-3 1-2-2 1-3z"/>',
  'return': '<path d="M9 14l-4-4 4-4"/><path d="M5 10h9a5 5 0 0 1 0 10h-3"/>',
  'swap': '<path d="M4 8h13l-3-3M20 16H7l3 3"/>',
  'warn': '<path d="M12 3l10 18H2z"/><path d="M12 10v5M12 18v.5"/>',
  'zoom': '<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5M8 11h6M11 8v6"/>',
  'gear': '<circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2"/>',
  'help': '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7M12 17v.5"/>',
  'bell': '<path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z"/><path d="M10 20a2 2 0 0 0 4 0"/>',
  'sun': '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>',
  'textsize': '<path d="M4 18L9 6l5 12M6 14h6M15 18l2.5-6 2.5 6M16 16h3"/>',
  'signout': '<path d="M10 4H5v16h5M14 8l4 4-4 4M8 12h10"/>',
  'minus': '<path d="M5 12h14"/>',
  'eye': '<path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
  'ruler': '<path d="M3 17L17 3l4 4L7 21z"/><path d="M7 13l2 2M10 10l2 2M13 7l2 2"/>',
  'play': '<path d="M7 5v14l11-7z"/>',
  'sheet': '<path d="M4 20h16M6 4h12v12H6z"/>',
  'tray': '<path d="M3 13l2-8h14l2 8v6H3z"/><path d="M3 13h5l2 3h4l2-3h5"/>',
  'in': '<path d="M12 4v12M7 11l5 5 5-5M4 20h16"/>',
  'out': '<path d="M12 16V4M7 9l5-5 5 5M4 20h16"/>',
  'square': '<rect x="4" y="4" width="16" height="16" rx="3"/>',
  'wifi-off': '<path d="M3 3l18 18M5 10a12 12 0 0 1 4-2.5M14.5 7.5A12 12 0 0 1 19 10M8 14a7 7 0 0 1 3-1.7M13.5 12.5A7 7 0 0 1 16 14"/><circle cx="12" cy="18" r="1" fill="currentColor"/>',
  'cloud-check': '<path d="M7 18a4 4 0 0 1-.5-8A6 6 0 0 1 18 9a4 4 0 0 1 0 9H7z"/><path d="M9 13.5l2 2 4-4"/>',
  'laptop': '<rect x="4" y="5" width="16" height="11" rx="2"/><path d="M2 19h20"/>',
};
/** One Release D glyph (aria-hidden; the control around it carries the label). */
export const sf = (name, size = 20, sw = 1.8) => (SF[name]
  ? `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="p4-sf">${SF[name]}</svg>`
  : '');
export const SF_NAMES = Object.keys(SF);
